// src/components/Analytics.jsx
import React, { useEffect, useMemo, useState } from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts'
import { apiFetch, apiFetchBlob } from '../api.js'

function formatMoney(v) {
  const n = Number(v || 0)
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 })
}

function formatDate(s) {
  if (!s) return ''
  return String(s).slice(0, 10)
}

function todayStr() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoStr(n) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

function buildQueryString(dateRange, filters = {}) {
  const params = new URLSearchParams()
  if (dateRange.from) params.set('from', dateRange.from)
  if (dateRange.to) params.set('to', dateRange.to)
  if (filters.customerId) params.set('customer_uid', String(filters.customerId))
  if (filters.productId) params.set('product_id', String(filters.productId))
  if (filters.paymentMode) params.set('payment_mode', String(filters.paymentMode))
  if (filters.orderStatus) params.set('order_status', String(filters.orderStatus))
  if (filters.paymentStatus) params.set('payment_status', String(filters.paymentStatus))
  if (filters.dateType) params.set('date_type', String(filters.dateType))
  return params.toString()
}

async function exportCsv(type, dateRange, filters) {
  const qs = buildQueryString(dateRange, filters)
  const blob = await apiFetchBlob(`/api/analytics/export?type=${type}&${qs}`)
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${type}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

async function exportPdf(dateRange, filters) {
  const qs = buildQueryString(dateRange, filters)
  const blob = await apiFetchBlob(`/api/analytics/export-pdf?${qs}`)
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'analytics.pdf'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export default function Analytics() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dateRange, setDateRange] = useState({ from: daysAgoStr(30), to: todayStr() })
  const [summary, setSummary] = useState(null)
  const [salesSeries, setSalesSeries] = useState([])
  const [topProducts, setTopProducts] = useState([])
  const [topCustomers, setTopCustomers] = useState([])
  const [receivables, setReceivables] = useState([])
  const [stock, setStock] = useState([])
  const [cashflow, setCashflow] = useState([])
  const [drill, setDrill] = useState({ title: '', items: [] })
  const [filtersData, setFiltersData] = useState({
    customers: [],
    products: [],
    payment_modes: [],
    order_statuses: [],
    payment_statuses: [],
    currency: 'XOF',
    currency_symbol: 'FCFA',
  })
  const [filters, setFilters] = useState({
    customerId: '',
    productId: '',
    paymentMode: '',
    orderStatus: '',
    paymentStatus: '',
    dateType: 'created_at',
  })

  const query = useMemo(() => {
    const qs = buildQueryString(dateRange, filters)
    return qs ? `?${qs}` : ''
  }, [dateRange, filters])
  const salesChart = useMemo(() => salesSeries.map((r) => ({
    ...r,
    dayLabel: formatDate(r.day),
    total_sales: Number(r.total_sales || 0),
    orders_count: Number(r.orders_count || 0),
  })), [salesSeries])
  const cashflowChart = useMemo(() => cashflow.map((r) => ({
    ...r,
    dayLabel: formatDate(r.day),
    inflow: Number(r.inflow || 0),
    outflow: Number(r.outflow || 0),
    net: Number(r.net || 0),
  })), [cashflow])
  const topProductsChart = useMemo(() => topProducts.slice(0, 10).map((r) => ({
    ...r,
    qty: Number(r.qty || 0),
    total: Number(r.total || 0),
  })), [topProducts])
  const topCustomersChart = useMemo(() => topCustomers.slice(0, 10).map((r) => ({
    ...r,
    orders_count: Number(r.orders_count || 0),
    total: Number(r.total || 0),
  })), [topCustomers])

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')

    Promise.all([
      apiFetch('/api/analytics/filters'),
      apiFetch(`/api/analytics/summary${query}`),
      apiFetch(`/api/analytics/sales-series${query}`),
      apiFetch(`/api/analytics/top-products${query}`),
      apiFetch(`/api/analytics/top-customers${query}`),
      apiFetch(`/api/analytics/receivables${query}`),
      apiFetch(`/api/analytics/stock`),
      apiFetch(`/api/analytics/cashflow${query}`),
    ])
      .then(([filtersResp, sum, series, products, customers, recv, stk, cash]) => {
        if (!alive) return
        if (filtersResp) setFiltersData(filtersResp)
        setSummary(sum)
        setSalesSeries(series?.series || [])
        setTopProducts(products?.items || [])
        setTopCustomers(customers?.items || [])
        setReceivables(recv?.items || [])
        setStock(stk?.items || [])
        setCashflow(cash?.series || [])
      })
      .catch((err) => {
        if (!alive) return
        setError(err?.message || 'Erreur chargement analytics')
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })

    return () => { alive = false }
  }, [query])

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-sm text-slate-500">Chargement des analytics…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xl font-bold text-slate-900">Analytics</div>
          <div className="text-xs text-slate-500">
            Fenêtre: {summary?.range?.from} → {summary?.range?.to}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="flex items-center gap-1">
            {[
              { label: '7j', days: 7 },
              { label: '30j', days: 30 },
              { label: '90j', days: 90 },
              { label: '1 an', days: 365 },
            ].map(({ label, days: n }) => {
              const active = dateRange.from === daysAgoStr(n) && dateRange.to === todayStr()
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setDateRange({ from: daysAgoStr(n), to: todayStr() })}
                  className={`px-2 py-1 rounded-lg border text-xs font-semibold transition ${
                    active
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <span>Du</span>
            <input
              type="date"
              value={dateRange.from}
              max={dateRange.to || todayStr()}
              onChange={(e) => setDateRange((r) => ({ ...r, from: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-900"
            />
            <span>au</span>
            <input
              type="date"
              value={dateRange.to}
              min={dateRange.from}
              max={todayStr()}
              onChange={(e) => setDateRange((r) => ({ ...r, to: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-900"
            />
          </div>
          <button
            type="button"
            onClick={() => exportPdf(dateRange, filters)}
            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
          >
            Export PDF
          </button>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-4 space-y-3">
        <div className="text-sm font-semibold">Filtres avancés</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Client</span>
            <select
              value={filters.customerId}
              onChange={(e) => setFilters((f) => ({ ...f, customerId: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1"
            >
              <option value="">Tous</option>
              {filtersData.customers.map((c) => (
                <option key={c.uid || c.id} value={c.uid || c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Produit</span>
            <select
              value={filters.productId}
              onChange={(e) => setFilters((f) => ({ ...f, productId: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1"
            >
              <option value="">Tous</option>
              {filtersData.products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Mode paiement</span>
            <select
              value={filters.paymentMode}
              onChange={(e) => setFilters((f) => ({ ...f, paymentMode: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1"
            >
              <option value="">Tous</option>
              {filtersData.payment_modes.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Statut commande</span>
            <select
              value={filters.orderStatus}
              onChange={(e) => setFilters((f) => ({ ...f, orderStatus: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1"
            >
              <option value="">Tous</option>
              {filtersData.order_statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Statut paiement</span>
            <select
              value={filters.paymentStatus}
              onChange={(e) => setFilters((f) => ({ ...f, paymentStatus: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1"
            >
              <option value="">Tous</option>
              {filtersData.payment_statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span>Devise: {filtersData.currency} ({filtersData.currency_symbol})</span>
            <label className="flex items-center gap-1">
              <span>Filtrer par:</span>
              <select
                value={filters.dateType || 'created_at'}
                onChange={(e) => setFilters((f) => ({ ...f, dateType: e.target.value }))}
                className="rounded border border-slate-200 bg-white px-2 py-1 font-medium"
              >
                <option value="sale_date">Date de vente</option>
                <option value="created_at">Date d'enregistrement</option>
              </select>
            </label>
          </div>
          <button
            type="button"
            onClick={() => setFilters({
              customerId: '',
              productId: '',
              paymentMode: '',
              orderStatus: '',
              paymentStatus: '',
              dateType: 'created_at',
            })}
            className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-slate-500">CA</div>
          <div className="text-lg font-semibold">{formatMoney(summary?.total_sales)}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-slate-500">Commandes</div>
          <div className="text-lg font-semibold">{summary?.orders_count || 0}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-slate-500">Panier moyen</div>
          <div className="text-lg font-semibold">{formatMoney(summary?.avg_order)}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-slate-500">Encaissements</div>
          <div className="text-lg font-semibold">{formatMoney(summary?.total_paid)}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-slate-500">Dépenses</div>
          <div className="text-lg font-semibold">{formatMoney(summary?.total_expenses)}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-slate-500">Cash net</div>
          <div className="text-lg font-semibold">{formatMoney(summary?.net_cash)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Ventes par jour</div>
            <button
              type="button"
              onClick={() => exportCsv('sales-series', dateRange, filters)}
              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Export CSV
            </button>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dayLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend />
                <Line type="monotone" dataKey="total_sales" name="CA" stroke="#2563eb" strokeWidth={2} />
                <Line type="monotone" dataKey="orders_count" name="Cmd" stroke="#0f172a" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="max-h-64 overflow-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left py-1">Date</th>
                  <th className="text-right py-1">CA</th>
                  <th className="text-right py-1">Cmd</th>
                </tr>
              </thead>
              <tbody>
                {salesSeries.map((r) => (
                  <tr key={r.day} className="border-t">
                    <td className="py-1">{formatDate(r.day)}</td>
                    <td className="py-1 text-right">{formatMoney(r.total_sales)}</td>
                    <td className="py-1 text-right">{r.orders_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Cashflow</div>
            <button
              type="button"
              onClick={() => exportCsv('cashflow', dateRange, filters)}
              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Export CSV
            </button>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cashflowChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dayLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend />
                <Bar dataKey="inflow" name="Entrées" fill="#16a34a" />
                <Bar dataKey="outflow" name="Sorties" fill="#ef4444" />
                <Bar dataKey="net" name="Net" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="max-h-64 overflow-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left py-1">Date</th>
                  <th className="text-right py-1">Entrées</th>
                  <th className="text-right py-1">Sorties</th>
                  <th className="text-right py-1">Net</th>
                </tr>
              </thead>
              <tbody>
                {cashflow.map((r) => (
                  <tr key={r.day} className="border-t">
                    <td className="py-1">{formatDate(r.day)}</td>
                    <td className="py-1 text-right">{formatMoney(r.inflow)}</td>
                    <td className="py-1 text-right">{formatMoney(r.outflow)}</td>
                    <td className="py-1 text-right">{formatMoney(r.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Top produits</div>
            <button
              type="button"
              onClick={() => exportCsv('top-products', dateRange, filters)}
              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Export CSV
            </button>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topProductsChart} layout="vertical" margin={{ left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="product_name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend />
                <Bar dataKey="total" name="CA" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="max-h-64 overflow-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left py-1">Produit</th>
                  <th className="text-right py-1">Qté</th>
                  <th className="text-right py-1">CA</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((r, i) => (
                  <tr key={`${r.product_name}-${i}`} className="border-t">
                    <td className="py-1">
                      <button
                        type="button"
                        className="text-slate-900 hover:underline"
                        onClick={() => {
                          const qs = buildQueryString(dateRange, { ...filters, productId: r.product_id })
                          apiFetch(`/api/analytics/orders?${qs}`)
                            .then((d) => setDrill({ title: `Commandes - ${r.product_name}`, items: d.items || [] }))
                        }}
                      >
                        {r.product_name}
                      </button>
                    </td>
                    <td className="py-1 text-right">{r.qty}</td>
                    <td className="py-1 text-right">{formatMoney(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Top clients</div>
            <button
              type="button"
              onClick={() => exportCsv('top-customers', dateRange, filters)}
              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Export CSV
            </button>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topCustomersChart} layout="vertical" margin={{ left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="customer_name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend />
                <Bar dataKey="total" name="CA" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="max-h-64 overflow-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left py-1">Client</th>
                  <th className="text-right py-1">Cmd</th>
                  <th className="text-right py-1">CA</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((r, i) => (
                  <tr key={`${r.customer_name}-${i}`} className="border-t">
                    <td className="py-1">
                      <button
                        type="button"
                        className="text-slate-900 hover:underline"
                        onClick={() => {
                          const qs = buildQueryString(days, { ...filters, customerId: r.customer_uid })
                          apiFetch(`/api/analytics/orders?${qs}`)
                            .then((d) => setDrill({ title: `Commandes - ${r.customer_name}`, items: d.items || [] }))
                        }}
                      >
                        {r.customer_name}
                      </button>
                    </td>
                    <td className="py-1 text-right">{r.orders_count}</td>
                    <td className="py-1 text-right">{formatMoney(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Créances clients</div>
            <button
              type="button"
              onClick={() => exportCsv('receivables', dateRange, filters)}
              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Export CSV
            </button>
          </div>
          <div className="max-h-64 overflow-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left py-1">Client</th>
                  <th className="text-right py-1">Reste dû</th>
                </tr>
              </thead>
              <tbody>
                {receivables.map((r) => (
                  <tr key={r.order_id} className="border-t">
                    <td className="py-1">{r.customer_name}</td>
                    <td className="py-1 text-right">{formatMoney(r.amount_remaining)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Stock (bas en haut)</div>
            <button
              type="button"
              onClick={() => exportCsv('stock', dateRange, filters)}
              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Export CSV
            </button>
          </div>
          <div className="max-h-64 overflow-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left py-1">Produit</th>
                  <th className="text-right py-1">Stock</th>
                  <th className="text-right py-1">Min</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((r) => (
                  <tr key={r.product_id} className="border-t">
                    <td className="py-1">{r.name}</td>
                    <td className="py-1 text-right">{r.stock_quantity}</td>
                    <td className="py-1 text-right">{r.stock_min}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">Drill-down</div>
          <button
            type="button"
            onClick={() => exportCsv('summary', dateRange, filters)}
            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
          >
            Export résumé
          </button>
        </div>
        <div className="text-xs text-slate-500 mb-2">{drill.title || 'Clique un produit ou un client pour voir les commandes.'}</div>
        <div className="max-h-64 overflow-auto text-sm">
          <table className="w-full">
            <thead className="text-xs text-slate-500">
              <tr>
                <th className="text-left py-1">Date</th>
                <th className="text-left py-1">Client</th>
                <th className="text-right py-1">Total</th>
                <th className="text-right py-1">Reste dû</th>
              </tr>
            </thead>
            <tbody>
              {drill.items.map((r) => (
                <tr key={r.order_id} className="border-t">
                  <td className="py-1">{formatDate(r.order_date)}</td>
                  <td className="py-1">{r.customer_name}</td>
                  <td className="py-1 text-right">{formatMoney(r.total_ttc)}</td>
                  <td className="py-1 text-right">{formatMoney(r.amount_remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <div className="text-xs text-slate-400">
          Export PDF disponible via le bouton en haut à droite.
        </div>
      </div>
    </div>
  )
}
